/**
 * Grouped, collapsible, drag-and-drop grid of dashboard KPI cards.
 */

import * as React from 'react';
import {
  DndContext,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { usePreferencesConfig } from '@/hooks/usePreferencesConfig';
import { getDashboardStats } from '@/services/dashboard.service';
import type { DashboardStatsResponse } from '@/types/dashboard.types';
import type { KpiCardData, KpiGroup } from '@/types/kpi.types';
import {
  DEFAULT_DASHBOARD_STATS_DATE_FILTERS,
  buildDashboardStatsDateQuery,
  type DashboardStatsDateFilterState,
} from '@/utils/dashboard-stats-date-range-utils';
import { KpiCard } from '@/components/common/stats/KpiCard';
import { KpiFilters } from './KpiFilters';
import {
  COLLAPSED_KPI_CARD_COUNT,
  KPI_CARD_STAGGER_MS,
  KPI_LABELS,
  KPI_QUERY_KEYS,
} from './kpi-configs';
import {
  applyKpiDragEnd,
  buildKpiCards,
  buildKpiPrefsPayload,
  countHiddenKpis,
  groupKpiCards,
  isGroupedKpis,
  isKpiGroupCollapsible,
  isKpiGroupExpanded,
  orderKpiCards,
  resolveKpiFilterVisibility,
  resolveKpiLoadStatus,
  toKpiCardProps,
  toggleKpiGroupExpanded,
  toggleKpiVisibility,
  visibleCardsForGroup,
} from './kpi-utils';

/** Props for {@link SortableKpiCard}. */
interface SortableKpiCardProps {
  kpi: KpiCardData;
  editMode: boolean;
  onToggleVisibility?: () => void;
}

/** Draggable KPI card; visibility toggle is shown only in edit mode. */
const SortableKpiCard: React.FC<SortableKpiCardProps> = ({ kpi, editMode, onToggleVisibility }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: kpi.id,
    disabled: !editMode,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  function handleToggleClick(event: React.MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    onToggleVisibility?.();
  }

  function handleTogglePointerDown(event: React.PointerEvent): void {
    event.stopPropagation();
  }

  function renderVisibilityToggle(): React.ReactNode {
    if (!editMode || !onToggleVisibility) return null;
    return (
      <Button
        variant="outline"
        size="icon"
        icon={kpi.visible ? Minus : Plus}
        iconOnly
        onClick={handleToggleClick}
        onPointerDown={handleTogglePointerDown}
        className={cn(
          'visibility-toggle-button absolute -top-2 -right-2 z-10 !rounded-full !border-2',
          !kpi.visible && 'hidden-card',
        )}
      />
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'relative',
        editMode && 'cursor-grab active:cursor-grabbing',
        isDragging && 'opacity-40',
        !kpi.visible && editMode && 'card-hidden',
      )}
    >
      {renderVisibilityToggle()}
      <KpiCard {...toKpiCardProps(kpi)} />
    </div>
  );
};

/** Imperative save / cancel for the section edit chrome. */
export interface KpiGridHandle {
  saveAndClose: () => void;
  cancel: () => void;
}

/** Props for {@link KpiGrid}. */
interface KpiGridProps {
  editMode: boolean;
  onHiddenCountChange: (count: number) => void;
  onSavingChange: (isSaving: boolean) => void;
  onExitEdit: () => void;
  /** Filter toolbar open — owned by {@link KpiSection} so the chip sits in the header. */
  showFilters: boolean;
  /** Open or close the filter toolbar. */
  onShowFiltersChange: (show: boolean) => void;
  /** Whether any KPI filter is available (drives the header chip). */
  onHasFiltersChange: (hasFilters: boolean) => void;
}

/** KPI grid with its own date / comparison / establishment filters. */
export const KpiGrid = React.forwardRef<KpiGridHandle, KpiGridProps>(function KpiGrid(
  {
    editMode,
    onHiddenCountChange,
    onSavingChange,
    onExitEdit,
    showFilters,
    onShowFiltersChange,
    onHasFiltersChange,
  },
  ref,
) {
  const { saveConfig, isSaving: isSavingDashboardPrefs } = usePreferencesConfig('dashboard');
  const [dateFilters, setDateFilters] = React.useState<DashboardStatsDateFilterState>(
    DEFAULT_DASHBOARD_STATS_DATE_FILTERS,
  );
  const [showAll, setShowAll] = React.useState(false);
  const [expandedGroups, setExpandedGroups] = React.useState<Record<string, boolean>>({});
  const [activeId, setActiveId] = React.useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  /** Date range sent to `GET /dashboard/stats`. */
  const kpiApiQuery = React.useMemo(() => buildDashboardStatsDateQuery(dateFilters), [dateFilters]);

  const {
    elements: dashboardVisibleElements = [],
    allElements: dashboardElements = [],
    isLoading: isLoadingElements,
    refetch,
  } = useInterfaceElements('dashboard');

  const {
    data: dashboardStats,
    isLoading: isLoadingKpis,
    isError: isKpiError,
    refetch: refetchKpis,
  } = useQuery<DashboardStatsResponse>({
    queryKey: KPI_QUERY_KEYS.dashboard(kpiApiQuery),
    queryFn: () => getDashboardStats(kpiApiQuery),
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    placeholderData: (previousData) => previousData,
  });

  const filterVisibility = resolveKpiFilterVisibility(dashboardVisibleElements);

  React.useEffect(() => {
    onHasFiltersChange(filterVisibility.hasAnyFilter);
  }, [filterVisibility.hasAnyFilter, onHasFiltersChange]);
  const loadStatus = resolveKpiLoadStatus({
    isKpiError,
    isLoadingElements,
    isLoadingKpis,
    response: dashboardStats,
  });

  const kpis = React.useMemo(
    () =>
      buildKpiCards({
        elements: dashboardElements,
        response: dashboardStats,
        comparisonPeriod: dateFilters.comparisonPeriod,
      }),
    [dashboardElements, dashboardStats, dateFilters.comparisonPeriod],
  );

  const [items, setItems] = React.useState(kpis);
  const initialItemsRef = React.useRef<KpiCardData[]>([]);
  const prevEditModeRef = React.useRef(editMode);

  React.useEffect(() => {
    setItems(kpis);
  }, [kpis]);

  React.useEffect(() => {
    if (editMode && !prevEditModeRef.current) {
      initialItemsRef.current = items.map((item) => ({ ...item }));
      onShowFiltersChange(false);
    }
    prevEditModeRef.current = editMode;
  }, [editMode, items, onShowFiltersChange]);

  React.useEffect(() => {
    onHiddenCountChange(countHiddenKpis(items));
  }, [items, onHiddenCountChange]);

  React.useEffect(() => {
    onSavingChange(isSavingDashboardPrefs);
  }, [isSavingDashboardPrefs, onSavingChange]);

  const orderedItems = React.useMemo(() => orderKpiCards(items, editMode), [editMode, items]);
  const grouped = React.useMemo(() => isGroupedKpis(items), [items]);
  const groups = React.useMemo(() => groupKpiCards(orderedItems, grouped), [grouped, orderedItems]);
  const activeItem = activeId ? (items.find((kpi) => kpi.id === activeId) ?? null) : null;

  /** Track the dragged card for the overlay. */
  function handleDragStart(event: DragStartEvent): void {
    setActiveId(event.active.id as string);
  }

  /** Reorder within the same family; cross-group drops are ignored. */
  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;
    setItems(applyKpiDragEnd(items, active.id as string, over.id as string, grouped));
  }

  /** Flip a card's visible flag in local edit state. */
  function handleToggleVisibility(id: string): void {
    setItems((prevItems) => toggleKpiVisibility(prevItems, id));
  }

  /** Persist order and visibility, then exit edit mode. */
  function handleSaveAndClose(): void {
    saveConfig(buildKpiPrefsPayload(items), {
      onSuccess: async () => {
        await refetch();
        onExitEdit();
      },
    });
  }

  /** Restore the snapshot taken when edit started, then exit edit mode. */
  function handleCancel(): void {
    const toRestore = initialItemsRef.current.length > 0 ? [...initialItemsRef.current] : kpis;
    setItems(toRestore);
    onExitEdit();
  }

  React.useImperativeHandle(ref, () => ({
    saveAndClose: handleSaveAndClose,
    cancel: handleCancel,
  }));

  /** Reload KPI values after an error / empty state. */
  function handleRetry(): void {
    void refetchKpis();
  }

  /** Toggle one group, or `showAll` when cards are not grouped. */
  function applyGroupToggle(label: string): void {
    const next = toggleKpiGroupExpanded(grouped, label, showAll, expandedGroups);
    setShowAll(next.showAll);
    setExpandedGroups(next.expandedGroups);
  }

  /** Family heading (icon, label, count) — omitted on a flat grid. */
  function renderGroupHeader(group: KpiGroup): React.ReactNode {
    if (!group.label) return null;
    const GroupIcon = group.icon;
    return (
      <div className="flex items-center gap-2 text-[var(--color-1-nuance-1)]">
        {GroupIcon ? <GroupIcon className="h-4 w-4" /> : null}
        <h3 className="text-sm font-semibold uppercase tracking-wide">{group.label}</h3>
        <span className="text-xs font-normal text-muted-foreground">{group.cards.length}</span>
      </div>
    );
  }

  /** One staggered cell wrapping a sortable KPI card. */
  function renderKpiCell(kpi: KpiCardData, index: number): React.ReactNode {
    function handleToggle(): void {
      handleToggleVisibility(kpi.id);
    }

    return (
      <div
        key={kpi.id}
        className="animate-fade-in-up animate-stagger"
        style={{ animationDelay: `${index * KPI_CARD_STAGGER_MS}ms` }}
      >
        <SortableKpiCard kpi={kpi} editMode={editMode} onToggleVisibility={handleToggle} />
      </div>
    );
  }

  /** Map cards to cells without passing the renderer as a `.map` callback. */
  function renderKpiCells(cards: KpiCardData[]): React.ReactNode {
    return cards.map((kpi, index) => renderKpiCell(kpi, index));
  }

  /** "See more / see less" under a collapsed family. */
  function renderCollapseControl(group: KpiGroup, expanded: boolean): React.ReactNode {
    if (!isKpiGroupCollapsible(group, editMode)) return null;

    function handleToggle(): void {
      applyGroupToggle(group.label);
    }

    return (
      <div className="flex justify-center pt-2">
        <Button
          variant="ghost"
          onClick={handleToggle}
          icon={expanded ? ChevronUp : ChevronDown}
          iconPosition="left"
        >
          {expanded
            ? KPI_LABELS.seeLess
            : KPI_LABELS.seeMore(group.cards.length - COLLAPSED_KPI_CARD_COUNT)}
        </Button>
      </div>
    );
  }

  /** One family (or the flat grid) with its sortable cards. */
  function renderGroup(group: KpiGroup): React.ReactNode {
    const expanded = isKpiGroupExpanded(group.label, grouped, expandedGroups, showAll);
    const cards = visibleCardsForGroup(group, editMode, expanded);
    const cardIds = cards.map((kpi) => kpi.id);

    return (
      <div key={group.label || 'all'} className="space-y-4">
        {renderGroupHeader(group)}
        <SortableContext items={cardIds}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {renderKpiCells(cards)}
          </div>
        </SortableContext>
        {renderCollapseControl(group, expanded)}
      </div>
    );
  }

  /** Map families without passing the renderer as a `.map` callback. */
  function renderGroups(): React.ReactNode {
    return groups.map((group) => renderGroup(group));
  }

  /** Drag overlay card that follows the pointer. */
  function renderDragOverlay(): React.ReactNode {
    if (!activeItem) return null;
    return (
      <div className="drag-overlay-base cursor-grabbing">
        <KpiCard {...toKpiCardProps(activeItem)} />
      </div>
    );
  }

  /** Date-filter toolbar under the header chip. */
  function renderFilters(): React.ReactNode {
    if (!showFilters) return null;
    return (
      <KpiFilters
        filters={dateFilters}
        onChange={setDateFilters}
        showPeriodFilter={filterVisibility.showPeriodFilter}
        showDateRangeFilter={filterVisibility.showDateRangeFilter}
        showComparisonFilter={filterVisibility.showComparisonFilter}
        showEstablishmentFilter={filterVisibility.showEstablishmentFilter}
      />
    );
  }

  /** Loading spinner while IE or KPIs are fetching. */
  function renderLoading(): React.ReactNode {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" label={KPI_LABELS.loading} />
      </div>
    );
  }

  /** Error or empty-KPI panel with retry. */
  function renderLoadError(): React.ReactNode {
    const errorMessage =
      loadStatus === 'empty'
        ? getErrorMessage('statsUnavailable')
        : getErrorMessage('loadStatsFailed');
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-8 text-center space-y-4">
        <p className="text-sm text-muted-foreground">{errorMessage}</p>
        <Button variant="outline" onClick={handleRetry}>
          {KPI_LABELS.retry}
        </Button>
      </div>
    );
  }

  /** Status panel while loading / error, otherwise the sortable cards. */
  function renderCards(): React.ReactNode {
    if (loadStatus === 'loading') return renderLoading();
    if (loadStatus !== 'ready') return renderLoadError();
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        autoScroll={{ enabled: true, threshold: { x: 0.1, y: 0.15 } }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="space-y-8">{renderGroups()}</div>
        <DragOverlay dropAnimation={{ duration: 200, easing: 'ease-out' }}>
          {renderDragOverlay()}
        </DragOverlay>
      </DndContext>
    );
  }

  return (
    <>
      {renderFilters()}
      {renderCards()}
    </>
  );
});
